import './App.scss';
import { ApiProvider } from '@cascade/react/api';
import { I18nProvider } from '@cascade/react/i18n';
import { QueryProvider } from '@cascade/react/query';
import { TermsProvider } from '@cascade/react/terms';
import { UserProvider } from '@cascade/react/user';
import { AuthenticationProvider } from '@cascade/react-dom/authentication';
import { ProjectsProvider } from '@cascade/react-dom/projects';
import { reportError } from '@cascade/react-dom/observability';
import {
  useConsumedSearchParams,
  usePwaInstall,
  useServiceWorker,
  useUserClient,
} from '@cascade/react-dom/hooks';
import { ThemeProvider, ToastProvider } from '@cascade/ui-library';
import { useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { createClients } from './App.helpers.ts';
import { deployment, PROJECT_ID_PARAM, serviceWorker } from '#config/index.ts';
import { enUS } from '#i18n/index.ts';
import { ObservabilityIdentityProvider } from '#providers/index.ts';
import { routeObjects } from '#routes.tsx';

const launchParams = [PROJECT_ID_PARAM];
const createRouter = () => createBrowserRouter(routeObjects);

export const App = () => {
  const launch = useConsumedSearchParams(launchParams);
  const requestedProjectId = launch.get(PROJECT_ID_PARAM) ?? undefined;
  const { isMobile, locale } = useUserClient();
  const isServiceWorkerEnabled = serviceWorker.isEnabled && isMobile;
  const [router] = useState(createRouter);
  const [{ api, auth }] = useState(() => createClients(deployment.apiUrl));

  useServiceWorker({ ...serviceWorker, isEnabled: isServiceWorkerEnabled });
  usePwaInstall({ isEnabled: isMobile });

  return (
    <ApiProvider api={api}>
      <I18nProvider locale={locale} messages={enUS}>
        <ThemeProvider>
          <ToastProvider>
            <AuthenticationProvider auth={auth}>
              <QueryProvider
                reporter={reportError}
                onUnauthenticated={auth.expire}
              >
                <UserProvider>
                  <ProjectsProvider
                    scope='user'
                    requestedProjectId={requestedProjectId}
                  >
                    <TermsProvider>
                      <ObservabilityIdentityProvider>
                        <RouterProvider router={router} />
                      </ObservabilityIdentityProvider>
                    </TermsProvider>
                  </ProjectsProvider>
                </UserProvider>
              </QueryProvider>
            </AuthenticationProvider>
          </ToastProvider>
        </ThemeProvider>
      </I18nProvider>
    </ApiProvider>
  );
};
